import { forwardRef } from 'react';
import styled from '@emotion/styled';
import type { CenterProps, SpacingValue } from './Center.types';

/**
 * Spacing map based on production theme
 */
const spacingMap: Record<number, string> = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
};

/**
 * Helper to resolve spacing value
 */
const resolveSpacing = (value: SpacingValue | undefined): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') {
    return spacingMap[value] || `${value * 4}px`;
  }
  return value;
};

/**
 * Styled Center container
 */
const StyledCenter = styled.div<{
  $inline: boolean;
  $pt?: string;
  $pr?: string;
  $pb?: string;
  $pl?: string;
  $mt?: string;
  $mr?: string;
  $mb?: string;
  $ml?: string;
}>`
  display: ${({ $inline }) => ($inline ? 'inline-flex' : 'flex')};
  align-items: center;
  justify-content: center;
  padding-top: ${({ $pt }) => $pt};
  padding-right: ${({ $pr }) => $pr};
  padding-bottom: ${({ $pb }) => $pb};
  padding-left: ${({ $pl }) => $pl};
  margin-top: ${({ $mt }) => $mt};
  margin-right: ${({ $mr }) => $mr};
  margin-bottom: ${({ $mb }) => $mb};
  margin-left: ${({ $ml }) => $ml};
`;

/**
 * Center component - A centered container (flex with center alignment)
 *
 * @example
 * ```tsx
 * // Center a spinner
 * <Center p={4}>
 *   <Spinner />
 * </Center>
 *
 * // Full-page center
 * <Center style={{ height: '100vh' }}>
 *   <Logo />
 * </Center>
 *
 * // Inline centered content
 * <Center inline p={2}>
 *   <Icon />
 * </Center>
 * ```
 */
export const Center = forwardRef<HTMLElement, CenterProps>(
  (
    {
      children,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      inline = false,
      as: Component = 'div',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    // Resolve padding values with priority: individual > axis > all
    const resolvedPt = resolveSpacing(pt ?? py ?? p);
    const resolvedPr = resolveSpacing(pr ?? px ?? p);
    const resolvedPb = resolveSpacing(pb ?? py ?? p);
    const resolvedPl = resolveSpacing(pl ?? px ?? p);

    // Resolve margin values with priority: individual > axis > all
    const resolvedMt = resolveSpacing(mt ?? my ?? m);
    const resolvedMr = resolveSpacing(mr ?? mx ?? m);
    const resolvedMb = resolveSpacing(mb ?? my ?? m);
    const resolvedMl = resolveSpacing(ml ?? mx ?? m);

    return (
      <StyledCenter
        as={Component}
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        $inline={inline}
        $pt={resolvedPt}
        $pr={resolvedPr}
        $pb={resolvedPb}
        $pl={resolvedPl}
        $mt={resolvedMt}
        $mr={resolvedMr}
        $mb={resolvedMb}
        $ml={resolvedMl}
        data-testid={dataTestId || testId}
        data-id={dataId}
        {...rest}
      >
        {children}
      </StyledCenter>
    );
  }
);

Center.displayName = 'Center';

export default Center;
